import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { API_URL } from '../../utils/api';
import './AdminInvites.css';

/**
 * Admin: invite-kodhantering — Sprint 3 av Fluxer-migrationen.
 *
 * Generera, lista och avaktivera invite-koder. auto_approve=True ger ny
 * spelare access_status=active direkt vid inlösen; annars måste de
 * fortfarande godkännas manuellt via AdminAccess.
 */
export default function AdminInvites({ perms, hideTitle = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { guildId } = usePermissions();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    note: '', max_uses: 1, auto_approve: true, expires_in_days: '',
  });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchInvites = useCallback(async () => {
    if (!guildId) return;
    setError('');
    try {
      const r = await fetch(
        `${API_URL}/api/admin/invites?guild_id=${guildId}`,
        { headers: getAuthHeaders() },
      );
      if (r.ok) {
        const data = await r.json();
        setInvites(data.invites || []);
      } else {
        setError(t('admin.loadInvitesFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, guildId, t]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    let expiresAt = null;
    const days = parseInt(form.expires_in_days, 10);
    if (Number.isFinite(days) && days > 0) {
      const d = new Date();
      d.setDate(d.getDate() + days);
      expiresAt = d.toISOString();
    }
    try {
      const r = await fetch(`${API_URL}/api/admin/invites?guild_id=${guildId}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: form.note,
          max_uses: parseInt(form.max_uses, 10) || 1,
          auto_approve: form.auto_approve,
          expires_at: expiresAt,
        }),
      });
      if (r.ok) {
        setForm({ note: '', max_uses: 1, auto_approve: true, expires_in_days: '' });
        fetchInvites();
      } else {
        const data = await r.json();
        setError(data.error || t('admin.createInviteFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (code) => {
    if (!window.confirm(t('admin.confirmDeactivate', { code }))) return;
    try {
      const r = await fetch(
        `${API_URL}/api/admin/invites/${code}?guild_id=${guildId}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (r.ok) {
        fetchInvites();
      } else {
        setError(t('admin.deactivateFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    }
  };

  const handleCopyLink = async (code) => {
    const url = `${window.location.origin}/pending?invite=${code}`;
    const ok = await _writeToClipboard(url);
    if (ok) {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } else {
      setError(t('admin.copyFailed', { url }));
    }
  };

  /**
   * Kopiera text till urklipp med fallback för icke-secure-contexts.
   * navigator.clipboard kräver HTTPS eller localhost. Faller tillbaka
   * till deprecated document.execCommand('copy') över en hidden textarea
   * när clipboard API inte är tillgänglig.
   */
  const _writeToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through till fallback
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  return (
    <div className="admin-invites">
      {!hideTitle && <h1 className="admin-section-title">{t('admin.subInvites')}</h1>}
      <p className="admin-section-help">
        {t('admin.invitesHelp')}
      </p>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.createNewInvite')}</div>
        <div className="invite-form">
          <label className="invite-form-row">
            <span className="invite-form-label">{t('admin.inviteNoteLabel')}</span>
            <input
              className="admin-input"
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder={t('admin.invitePlaceholderNote')}
              maxLength={255}
            />
          </label>
          <label className="invite-form-row">
            <span className="invite-form-label">{t('admin.inviteUses')}</span>
            <select
              className="admin-input"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value, 10) })}
            >
              <option value={1}>{t('admin.use1')}</option>
              <option value={3}>{t('admin.use3')}</option>
              <option value={5}>{t('admin.use5')}</option>
              <option value={10}>{t('admin.use10')}</option>
              <option value={0}>{t('admin.useUnlimited')}</option>
            </select>
          </label>
          <label className="invite-form-row">
            <span className="invite-form-label">{t('admin.inviteExpiry')}</span>
            <input
              className="admin-input"
              type="number"
              min="1"
              max="365"
              value={form.expires_in_days}
              onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
              placeholder={t('admin.inviteExpiryPlaceholder')}
            />
          </label>
          <label className="invite-form-checkbox">
            <input
              type="checkbox"
              checked={form.auto_approve}
              onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })}
            />
            <span>{t('admin.inviteAutoApprove')}</span>
          </label>
          <button
            className="admin-btn"
            onClick={handleCreate}
            disabled={creating}
            type="button"
          >
            {creating ? t('admin.creating') : t('admin.createInvite')}
          </button>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.inviteActiveHistory')}</div>
        {loading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : invites.length === 0 ? (
          <div className="admin-info">{t('admin.noInvites')}</div>
        ) : (
          <table className="invite-table">
            <thead>
              <tr>
                <th>{t('admin.colCode')}</th>
                <th>{t('admin.colNote')}</th>
                <th>{t('admin.colUses')}</th>
                <th>{t('admin.colAuto')}</th>
                <th>{t('admin.colStatus')}</th>
                <th>{t('admin.colExpires')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id} className={inv.is_valid ? '' : 'invite-row-inactive'}>
                  <td className="invite-code">{inv.code}</td>
                  <td>{inv.note || <span className="invite-muted">—</span>}</td>
                  <td>
                    {inv.times_used} / {inv.max_uses === 0 ? '∞' : inv.max_uses}
                  </td>
                  <td>{inv.auto_approve ? '✓' : '—'}</td>
                  <td>
                    {inv.is_valid ? (
                      <span className="invite-pill invite-pill-active">{t('admin.pillActive')}</span>
                    ) : !inv.is_active ? (
                      <span className="invite-pill invite-pill-inactive">{t('admin.pillDeactivated')}</span>
                    ) : (
                      <span className="invite-pill invite-pill-inactive">{t('admin.pillExpired')}</span>
                    )}
                  </td>
                  <td className="invite-muted">
                    {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('sv-SE') : '—'}
                  </td>
                  <td className="invite-actions">
                    {inv.is_valid && (
                      <>
                        <button
                          className="admin-btn admin-btn-secondary"
                          onClick={() => handleCopyLink(inv.code)}
                          type="button"
                        >
                          {copied === inv.code ? t('admin.copied') : t('admin.copyLink')}
                        </button>
                        <button
                          className="admin-btn admin-btn-danger"
                          onClick={() => handleDeactivate(inv.code)}
                          type="button"
                        >
                          {t('admin.deactivate')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
